import cloneDeep from 'lodash/cloneDeep';
import type Luminary from '@lumen5/luminary';
import type { AspectRatio, Asset, Scene, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { Renderer } from '@lumen5/luminary/libs/Lottie/types/lottie';

import {
    DEFAULT_ASPECT_RATIO,
    DEFAULT_TAB_ID,
    QueryParams,
    SCENE_EDITOR_BASE_URL,
    STANDARD_TRANSITIONS,
} from './constants';

import { TemplateStatusEnum } from 'js/src/client';

/**
 * Get a value from an object at a property path.
 *
 * @param  {object} object
 * @param  {string} path - A dot separated path, prefixed with a '.' which specifies the root.
 * @returns {*|undefined} The value or undefined if the path is invalid.
 * @example getObjectPropertyValue(o, '.size.width')
 */
export function getObjectPropertyValue(object: object, path: string) {
    const pathComponents = path.split('.');
    return getProperty(object, pathComponents);
}

/**
 * Set a value to an object at a property path.
 *
 * If the path is invalid the object is unaffected.
 *
 * @param  {object} object
 * @param  {string} path - A dot separated path, prefixed with a '.' which specifies the root.
 * @param  {*} value - The value to set at the path.
 * @returns {*|undefined} The value that was set, or undefined if setting failed due to invalid path.
 */
export function setObjectPropertyValue(object: object, path: string, value: unknown) {
    const pathComponents = path.split('.');

    if (propertyExists(object, pathComponents)) {
        return setProperty(object, pathComponents, value);
    }

    return undefined;
}

/**
 * Clamps the value to the given range.
 *
 * @param  {number[]} range - a range [a, b] where a <= b.
 * @param  {number} value
 */
export function clamp(range: [number, number], value: number) {
    return Math.max(range[0], Math.min(range[1], value));
}
/**
 * Make a consistent label out of some "code like" text.
 *
 * `text_position` becomes  `text position`.
 * `textPosition` becomes `text position`.
 *
 * @param  {string} text
 * @param {boolean} titleCased If true, `textPosition` becomes `Text Position`.
 */
export function makeLabel(text: string, titleCased = false) {
    let result = text.split('_').join(' ');

    result = result.replace(/[A-Z][a-z]+/g, ' $&').toLowerCase();

    if (titleCased) {
        result = result
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    return result;
}

/**
 * Given a path like 'size.width', create a human readable label suitable for a UI.
 *
 * Currently simply returns the last path component: `size.width` becomes `width`.
 *
 * @param  {string} path
 */
export function makeLabelFromPath(path: string) {
    const pathComponents = path.split('.');
    const lastPathComponent = pathComponents[pathComponents.length - 1];
    return makeLabel(lastPathComponent);
}

/**
 * Convert RGB integer value to RGB Array
 *
 * @param {number} number - RGB integer representation of the color.
 * @returns {number[]} RGB array representation of the color
 */
export function int2rgb(number: number) {
    const b = number & 0xff;
    const g = (number & 0xff00) >>> 8;
    const r = (number & 0xff0000) >>> 16;
    return [r, g, b];
}

/**
 * Convert RGB array value to RGB integer
 *
 * @param {number[]} rgbArray - RGB array representation of the color
 * @returns {number} RGB integer representation of the color.
 */
export function rgb2int(rgbArray: number[]) {
    return 65536 * rgbArray[0] + 256 * rgbArray[1] + rgbArray[2];
}

/**
 * Test if the given text is JSON.
 *
 * @param  {string} text
 * @return {boolean} `true` if the text is JSON.
 */
export function isJSON(text: string | null | undefined) {
    if (!text) {
        return false;
    }
    try {
        JSON.parse(text);
        return true;
    } catch (_e) {
        return false;
    }
}

type ClipboardItem = {
    key: string;
    data: any;
    path: string;
};
const isClipboardItem = (item: any): item is ClipboardItem => {
    return item && item.key && item.data;
};
const isClipboardItemList = (item: unknown): item is ClipboardItem[] => {
    return Array.isArray(item) && item.every(isClipboardItem);
};

/**
 * Write JSON object into the clipboard with a given key.
 * @param key lookup key for the JSON object
 * @param data JSON object to write
 * @param path stores an additional key, named path, to the JSON object
 * @param extend true if we want to extend the clipboard
 */
export const writeJSONtoClipboard = async (
    key: string,
    data: any,
    path?: string,
    extend = true,
) => {
    const type = 'text/plain';
    const newEntry = {
        data,
        key,
        path,
    };
    let list = [];
    if (extend) {
        const entries = await readJSONsfromClipboard();
        list = [newEntry, ...entries];
    } else {
        list = [newEntry];
    }
    // NOTE: all blobParts data get concatenated into one long string during creation so we can't easily use it to
    // separate out our data.
    const part = JSON.stringify(list);
    const blob = new Blob([part], { type });

    const permissionName = 'clipboard-write' as PermissionName;
    const result = await navigator.permissions.query({ name: permissionName });
    if (result.state === 'granted' || result.state === 'prompt') {
        const data = new ClipboardItem({ [type]: blob });
        // NOTE: even though data is an array it currently only supports 1 ClipboardItem
        await navigator.clipboard.write([data]);
    }
};

/**
 * Read JSON objects from clipboard which were written using writeJSONtoClipboard
 */
const readJSONsfromClipboard: () => Promise<ClipboardItem[]> = async () => {
    const permissionName = 'clipboard-read' as PermissionName;
    const result = await navigator.permissions.query({ name: permissionName });
    if (result.state === 'granted' || result.state === 'prompt') {
        const text = await navigator.clipboard.readText();
        try {
            const json = JSON.parse(text);
            if (isClipboardItemList(json)) {
                return json;
            }
            return [];
        } catch {
            return [];
        }
    }

    return [];
};

/**
 * Read the JSON object for the given key from the clipboard
 * @param {string} key lookup key for the JSON object
 */
export const readJSONfromClipboard = async (key: string) => {
    const clipboardItems = await readJSONsfromClipboard();
    for (let i = 0; i < clipboardItems.length; i++) {
        if (clipboardItems[i].key === key) {
            return clipboardItems[i].data;
        }
    }
    return null;
};

export const readLatestJSONfromClipboard = async () => {
    const clipboardItems = await readJSONsfromClipboard();
    return clipboardItems.at(0);
};

/**
 * Given a string, test if it matches the format of a scene design id, i.e. a colon separated template + variation.
 * We expect our ids to be alphanumeric (upper and lower case) and should contain one :. The use one or more - or _ is
 * supported. All other characters are NOT supported.
 * @param  {string} name The possible scene design name, e.g. 'be_yourself:text_1'.
 */
export const isValidSceneDesignId = (id: string, includesTemplate = true) => {
    if (!includesTemplate) {
        // partial scene design name (does not include template name)
        return !!id.match(/^[a-z0-9-_]+$/i);
    }
    if ((id.match(/:/g) || []).length !== 1) {
        // count :
        return false;
    }
    if (id.length === 1) {
        // = :
        return false;
    }
    return !!id.match(/^[a-z0-9-_:]+$/i);
};

/**
 * Given a string, test if it matches the format of a template id, i.e. a colon separated template. We expect our ids to
 * be alphanumeric (upper and lower case). The use one or more - or _ is supported. All other characters are NOT
 * supported.
 * @param  {string} name The possible template id, e.g. 'be_yourself'.
 */
export const isValidTemplateId = (id: string) => {
    return !!id.match(/^[a-z0-9-_]+$/i);
};

/**
 * Some properties with object datatypes, like sourceData, cannot have their properties set individually and
 * instead require the full object for an property change.  This ensures that properties with object datatypes
 * are passed as a unified object.
 *
 * @param object {Object}
 * @param path {string} - Full path of the object property being set
 * @param value {*} - Value to be set
 * @returns {Object}
 */
export function setAsObject(object: any, path: any, value: any) {
    const rootProperty = path.split('.')[0];
    const subProperty = path.substring(rootProperty.length + 1);
    const updatedObject = cloneDeep(object[rootProperty]);
    // update the specified property
    setObjectPropertyValue(updatedObject, subProperty, value);
    object[rootProperty] = updatedObject;
}

/**
 * Given a list of names, and a prefix, generate a unique name.
 *
 * For example, ['one', 'two'] and 'two' generates 'two_1'.
 */
export function uniqueName(prefix: string, names: string[]) {
    // Reduce the search space.
    names = names.filter((name) => name.startsWith(prefix));

    let count = 1;

    while (true) {
        const test = `${prefix}_${count}`;
        if (!names.find((name) => name.startsWith(test))) {
            return test;
        }
        count += 1;
    }
}

type NamePrefix =
    | 'media'
    | 'text'
    | 'color'
    | 'lottie'
    | 'group'
    | 'rectangle'
    | 'circle'
    | 'gradient'
    | 'icon'
    | 'fancyCaption';

export function getNamePrefixForAsset(asset: Asset): NamePrefix {
    if (VideoData.isMediaAsset(asset)) {
        return 'media';
    }
    if (VideoData.isTextAsset(asset)) {
        return 'text';
    }
    if (VideoData.isColorAsset(asset)) {
        return 'color';
    }
    if (VideoData.isLottieAsset(asset)) {
        return 'lottie';
    }
    if (VideoData.isAssetGroup(asset)) {
        return 'group';
    }
    if (VideoData.isRectangleAsset(asset)) {
        return 'rectangle';
    }
    if (VideoData.isCircleAsset(asset)) {
        return 'circle';
    }
    if (VideoData.isGradientAsset(asset)) {
        return 'gradient';
    }
    if (VideoData.isIconAsset(asset)) {
        return 'icon';
    }
    if (VideoData.isFancyCaptionAsset(asset)) {
        return 'fancyCaption';
    }
    throw new Error('Unknown asset type');
}

/**
 * scale all properties on a given child that typically change based on the aspect ratio
 */
function scaleProperties(
    child: any,
    currentAspectRatio: AspectRatio,
    targetAspectRatio: AspectRatio,
    widthScaleFactor: number,
    heightScaleFactor: number,
) {
    // from testing it appears that using the min scale factor works best for attributes which
    // aren't clearly tied to the x or y dimension (a circle's radius, for instance)
    const unclearDimensionScaleFactor = Math.min(widthScaleFactor, heightScaleFactor);
    if (child.maxWidth) {
        child.maxWidth *= widthScaleFactor;
    }
    if (child.maxHeight) {
        child.maxHeight *= heightScaleFactor;
    }
    if (child.textSize) {
        const aspectRatioFactor = 0.875;
        if (targetAspectRatio === VideoData.LANDSCAPE_ASPECT_RATIO) {
            // going from square or vertical to landscape so make the text bigger
            child.textSize /= aspectRatioFactor;
        } else if (currentAspectRatio === VideoData.LANDSCAPE_ASPECT_RATIO) {
            // going from landscape to square or vertical so make the text smaller
            child.textSize *= aspectRatioFactor;
        }
    }
    if (child.transformation?.position?.x) {
        child.transformation.position.x *= widthScaleFactor;
    }
    if (child.transformation?.position?.y) {
        child.transformation.position.y *= heightScaleFactor;
    }
    if (child.lineThickness) {
        child.lineThickness *= unclearDimensionScaleFactor;
    }
    if (child.radius) {
        child.radius *= unclearDimensionScaleFactor;
    }
    if (child.startRadius) {
        child.startRadius *= unclearDimensionScaleFactor;
    }
    if (child.endRadius) {
        child.endRadius *= unclearDimensionScaleFactor;
    }
    if (child.radiusOffset) {
        child.radiusOffset *= unclearDimensionScaleFactor;
    }
    if (child.rectWidth) {
        child.rectWidth *= widthScaleFactor;
    }
    if (child.rectHeight) {
        child.rectHeight *= heightScaleFactor;
    }
    if (child.childMaxHeightProportions) {
        // for actual object we need to update via `.array` as it's a `NumberArray`
        if (child.childMaxHeightProportions.array) {
            child.childMaxHeightProportions.array.forEach((val: number, index: number) => {
                child.childMaxHeightProportions.setValueAtIndex(val * heightScaleFactor, index);
            });
        }
        // for textPositionPresets we need to update the JS object
        else {
            child.childMaxHeightProportions.forEach((val: any, index: number) => {
                child.childMaxHeightProportions[index] *= heightScaleFactor;
            });
        }
    }
    // designers requested to NOT update 'spaceBetweenChildren'
    // https://lumen5.slack.com/archives/CENPE0S2H/p1638824389272500
    if (child.mask) {
        scaleProperties(
            child.mask,
            currentAspectRatio,
            targetAspectRatio,
            widthScaleFactor,
            heightScaleFactor,
        );
    }
}

/**
 * Resize the first scene in the video from one aspect ratio to another using logic. This method
 * doesn't promise a perfect result but it should cover basic resizing & repositioning.
 */
export function intelligentlyResizeSceneToAspectRatio(
    luminary: Luminary,
    targetAspectRatio: AspectRatio,
    sceneGroupIndex: number,
    sceneIndex: number,
) {
    // if the aspect ratio already matches, skip!
    if (luminary.aspectRatio === targetAspectRatio) {
        return;
    }

    // calculate scale factors
    const previousSceneWidth = luminary.referenceResolution.width;
    const previousSceneHeight = luminary.referenceResolution.height;
    const newResolution = VideoData.BASE_SLIDE_DIMENSIONS[targetAspectRatio];
    const widthScaleFactor = newResolution.width / previousSceneWidth;
    const heightScaleFactor = newResolution.height / previousSceneHeight;

    // for each slide child scale it's properties
    getActiveScene(luminary, sceneGroupIndex, sceneIndex)
        .getAssets()
        .forEach((child: any) => {
            const originalMaxHeight = child.maxHeight;
            const originalTextSize = child.textSize;

            scaleProperties(
                child,
                luminary.aspectRatio,
                targetAspectRatio,
                widthScaleFactor,
                heightScaleFactor,
            );

            if (VideoData.isAnyTextAsset(child as Asset)) {
                child.maxHeight = originalMaxHeight;
                child.textSize = originalTextSize;
            }
        });

    // scale each text position preset's properties
    const activeScene = getActiveScene(luminary, sceneGroupIndex, sceneIndex);
    Object.keys(activeScene.textPositionPresets).forEach((presetName) => {
        const preset = (activeScene.textPositionPresets as any)[presetName];
        preset.forEach((child: any) => {
            scaleProperties(
                child,
                luminary.aspectRatio,
                targetAspectRatio,
                widthScaleFactor,
                heightScaleFactor,
            );
        });
    });
}

/**
 * return the scene design config to save to disk
 * NOTE: we bundle this because we have to do some sanitization/fixes before
 * saving. If we can remove this sanitization we don't need to have this method!
 */
export function getConfigToSave(luminary: Luminary, sceneEditorActiveGroupIndex: number) {
    return luminary.scenes[sceneEditorActiveGroupIndex].save();
}

export function parseQueryString(params = ['template', 'aspectRatio', 'sceneDesign']) {
    const urlParams = new URLSearchParams(window.location.search);
    return Object.fromEntries(params.map((param) => [param, urlParams.get(param)]));
}

export function createHashCache(template: any, sceneDesign: any, aspectRatio: any) {
    // TODO: merge with updateQueryString, but make sure only required params stay
    // (we don't need the tab in template editor)
    // Or better: use react-router or another routing framework.
    const base = `?template=${template}&sceneDesign=${sceneDesign}`;
    const hasAspectRatio =
        aspectRatio != null && aspectRatio !== 'undefined' && aspectRatio !== 'null';
    return hasAspectRatio ? `${base}&aspectRatio=${aspectRatio}` : base;
}

export function getSceneDesignEditorLink({
    templateId,
    sceneDesignId,
    aspectRatio,
}: {
    templateId: string;
    sceneDesignId: string;
    aspectRatio: AspectRatio;
}) {
    const search = createHashCache(templateId, sceneDesignId, aspectRatio);
    return `/editor/scene-design/${search}`;
}

export function getTemplateEditorLink({
    templateId,
    sceneDesignId,
    aspectRatio,
}: {
    templateId: string;
    sceneDesignId?: string;
    aspectRatio?: AspectRatio;
}) {
    const search = createHashCache(templateId, sceneDesignId, aspectRatio);
    return `/editor/template${search}`;
}

export function setHashCache(template: any, sceneDesign: any, aspectRatio: any) {
    window.history.pushState({}, '', createHashCache(template, sceneDesign, aspectRatio));
}

export function getStatusColor(status: TemplateStatusEnum) {
    const colors = {
        [TemplateStatusEnum.DRAFT]: 'grey',
        [TemplateStatusEnum.ACTIVE]: 'green',
        [TemplateStatusEnum.ARCHIVED]: 'red',
    };
    return colors[status] || 'black';
}

export function getSaveButtonText(status: string | null | undefined, isSaving: boolean): string {
    switch (status) {
        case TemplateStatusEnum.ACTIVE:
            return isSaving ? 'Publishing...' : 'Publish';
        case TemplateStatusEnum.ARCHIVED:
            return isSaving ? 'Publishing...' : 'Publish';
        case TemplateStatusEnum.DRAFT:
        default:
            return isSaving ? 'Saving...' : 'Save';
    }
}

/**
 * Get the duration of given asset/element
 * @param {*} elem luminary asset/element
 * @returns {number}
 */
export function getScaledAnimationDuration(elem: any) {
    return elem.animationManager.introEnd;
}

/**
 * Returns true if the asset has one or more scaled dynamic animations. This can be for object animation,
 * color or alpha graphic.
 * @param {*} elem luminary asset/element
 * @returns {boolean}
 */
export function hasScaledDynamicStyle(elem: any) {
    if (!elem.hasDynamicStyle) {
        return false;
    }
    return (
        hasScaledObjectAnimation(elem) || hasScaledColorGraphic(elem) || hasScaledAlphaGraphic(elem)
    );
}

/**
 * Returns true if the asset has one or more scaled object animations.
 * @param {*} elem luminary asset/element
 * @returns {boolean}
 */
export function hasScaledObjectAnimation(elem: any) {
    if (!elem.hasObjectAnimation) {
        return false;
    }
    const dynamicAnimationConfigurations = [...elem.objectAnimation];
    const scaledDuration = getScaledAnimationDuration(elem);
    return checkConfigurationsForScaledAnimations(dynamicAnimationConfigurations, scaledDuration);
}

/**
 * Returns true if the asset has one or more scaled color graphics.
 * @param {*} elem luminary asset/element
 * @returns {boolean}
 */
export function hasScaledColorGraphic(elem: any) {
    if (!elem.hasColorGraphic) {
        return false;
    }
    const dynamicAnimationConfigurations = elem.colorGraphics.reduce((acc: any, curr: any) => {
        return acc.concat(...(curr.animation || []));
    }, []);
    const scaledDuration = getScaledAnimationDuration(elem);
    return checkConfigurationsForScaledAnimations(dynamicAnimationConfigurations, scaledDuration);
}

/**
 * Returns true if the asset has one or more scaled alpha graphics.
 * @param {*} elem luminary asset/element
 * @returns {boolean}
 */
export function hasScaledAlphaGraphic(elem: any) {
    if (!elem.hasAlphaGraphic) {
        return false;
    }
    const dynamicAnimationConfigurations = elem.alphaGraphics.reduce((acc: any, curr: any) => {
        return acc.concat(...(curr.animation || []));
    }, []);
    const scaledDuration = getScaledAnimationDuration(elem);
    return checkConfigurationsForScaledAnimations(dynamicAnimationConfigurations, scaledDuration);
}

/**
 * Checks given list of dynamic animation configurations for any scaled dynamic animations
 * @param {*} dynamicAnimationConfigurations list of dynamic animation configurations
 * @param {number} scaledDuration expected scaled duration to compare current duration against
 * @returns {boolean}
 */
export function checkConfigurationsForScaledAnimations(
    dynamicAnimationConfigurations: any[] = [],
    scaledDuration: number,
) {
    return dynamicAnimationConfigurations.some((config) =>
        checkConfigurationForScaledAnimations(config, scaledDuration),
    );
}

/**
 * Check given single dynamic animation configuration for any any scaled dynamic animations
 * @param {*} dynamicAnimationConfiguration single dynamic animation configuration
 * @param scaledDuration expected scaled duration to compare current duration against
 * @returns {boolean}
 */
export function checkConfigurationForScaledAnimations(
    dynamicAnimationConfiguration: any,
    scaledDuration: any,
) {
    const inputDuration =
        dynamicAnimationConfiguration.duration + dynamicAnimationConfiguration.delay;
    return inputDuration > scaledDuration;
}

export const getProperty = (object: any, propertyList: string[]) => {
    let result = object;

    // propertyList data can be represented as a single property "b" or as a point in
    // an array property "b[0]".  If a point in an array property is provided,
    // we'll need to remove the index number and characters from the property name
    // and then use their numerical representation as a proper array index.
    for (let i = 0; i < propertyList.length; i++) {
        if (!result) {
            return undefined;
        }
        // check for numbers surrounded by square brackets to determine if we're
        // dealing with an array property
        const indexData = propertyList[i].match(/\[([0-9]+)\]/);
        if (indexData) {
            // get the "name" of the property (without index notation)
            const arrayProperty = propertyList[i].slice(0, indexData.index);

            // use the "name" as the lookup key and the extracted numerical data as the index
            result = result[arrayProperty][indexData[1]];
            // return result;
        }
        // make sure we're dealing with a valid property
        else if (result === null || result === undefined) {
            return undefined;
        }
        // go down to the child property
        else if (propertyList[i] in result) {
            result = result[propertyList[i]];
        } else {
            return undefined;
        }
    }
    return result;
};

export const propertyExists = (object: any, propertyList: string[]) => {
    return getProperty(object, propertyList) !== undefined;
};

export const setProperty = (object: any, propertyList: any, value: any) => {
    const npropertyList = propertyList.slice();
    const s = npropertyList.pop();
    const inbetween = getProperty(object, npropertyList);

    // propertyList data can be represented as a single property "b" or as a point in
    // an array property "b[0]".  If a point in an array property is provided,
    // we'll need to remove the index number and characters from the property name
    // and then use their numerical representation as a proper array index.

    // check for numbers in property name to determine if we're dealing with an array property
    const indexData = s.match(/\[([0-9]+)\]/);
    if (indexData) {
        // get the "name" of the property (without index notation)
        npropertyList.push(s.slice(0, indexData.index));

        // get a reference to the array
        const propertyArray = getProperty(object, npropertyList);

        // set value at index and exit
        if (propertyArray[indexData[1]] !== value) {
            propertyArray[indexData[1]] = value;
        }
    } else {
        if (inbetween[s] !== value) {
            inbetween[s] = value;
        }
    }
};

/**
 * Checks that a list of Urls is valid.
 * @see validateUrls
 */
export function validateUrls(urls: string[]) {
    return Promise.all(urls.map(validateUrl));
}

/**
 * Check that they Url is valid by performing a HEAD request to see that the Url exists.
 */
export function validateUrl(url: string) {
    return new Promise<string>((resolve, reject) => {
        $.ajax({
            url: url,
            type: 'HEAD',
            success: () => {
                resolve(url);
            },
            error: (xhr) => {
                let errorMessage = `There is something wrong with URL ${url}. Typo? Are you online?`;
                if (xhr.status === 404) {
                    errorMessage = `File not found: ${url}`;
                }
                reject(new Error(errorMessage));
            },
        });
    });
}

export function updateQueryString(params: Record<string, string>) {
    const updatedParams: URLSearchParams = new URLSearchParams(window.location.search);
    Object.entries(params).forEach(([name, value]: [string, string | null]) => {
        if (!value) {
            updatedParams.delete(name);
        } else {
            updatedParams.set(name, value);
        }
    });
    if (!updatedParams.has(QueryParams.AspectRatio)) {
        updatedParams.set(QueryParams.AspectRatio, DEFAULT_ASPECT_RATIO);
    }
    if (!updatedParams.has(QueryParams.Tab)) {
        const tabId = updatedParams.has(QueryParams.AspectRatio)
            ? (updatedParams.get(QueryParams.AspectRatio) as string)
            : DEFAULT_TAB_ID;
        updatedParams.set(QueryParams.Tab, tabId);
    }
    return '?' + updatedParams.toString();
}

const excludePropertiesFromSerialization = (object: any, props: string[]) => {
    if (object['linkToSceneGroup']) {
        // we cannot directly access playbackRegions if an asset is linked to scene group
        props = props.filter((prop: string) => prop !== 'playbackRegions');
    }

    return props;
};
/**
 * Luminary's `save()` method serializes `propertiesToSerialize` AND `readonlyProperties` AND `editableProperties`.
 * That's confusing! We'll potentially cleanup (see https://lumen5.slack.com/archives/CENPE0S2H/p1663710476623649)
 * For now, this util helps keep it clean.
 * @param object
 */
export function getSerializedProperties(object: any) {
    let props = [];
    if (object.readonlyProperties) {
        props.push(...object.readonlyProperties);
    }
    if (object.propertiesToSerialize) {
        props.push(...object.propertiesToSerialize);
    }
    if (object.editableProperties) {
        props.push(...object.editableProperties);
    }
    props = excludePropertiesFromSerialization(object, props);
    return props;
}

export function checkLottieFileSize(renderer: Renderer, fileSize: number) {
    if (renderer !== VideoData.LOTTIE_RENDERER_SVG) {
        return;
    }
    if (fileSize <= VideoData.LOTTIE_RENDERER_SVG_MAX_FILESIZE * 1000) {
        return;
    }
    window.alert(`Lottie asset (${Math.round(fileSize / 1000)} kB) is too large`);
}

export function isSceneEditor() {
    return window.location.pathname === `${SCENE_EDITOR_BASE_URL}/`;
}

export function getActiveScene(
    luminary: Readonly<Luminary>,
    activeSceneGroupIndex: number,
    activeSceneIndex: number,
): Scene {
    const sceneGroup = getActiveSceneGroup(luminary, activeSceneGroupIndex);
    if (sceneGroup.scenes.length === 0) {
        throw new Error('No active scenes');
    }

    const scene = sceneGroup.scenes[activeSceneIndex];
    if (!scene) {
        throw new Error(`No scene at index ${activeSceneIndex} in active scene group`);
    }

    return scene;
}

export function getActiveSceneOrNull(
    luminary: Readonly<Luminary>,
    activeSceneGroupIndex: number,
    activeSceneIndex: number,
): Scene | null {
    const sceneGroup = luminary.sceneGroups?.[activeSceneGroupIndex];
    const scene = sceneGroup?.scenes[activeSceneIndex];
    if (!scene || scene.destroyed) {
        return null;
    }
    return scene;
}

export function getActiveSceneGroup(
    luminary: Readonly<Luminary>,
    sceneEditorActiveGroupIndex: number,
): SceneGroup {
    if (!luminary.sceneGroups) {
        throw new Error('No scene groups');
    }
    const sceneGroup = luminary.sceneGroups[sceneEditorActiveGroupIndex] || null;
    if (!sceneGroup) {
        throw new Error('No scene group');
    }
    return sceneGroup;
}

export function getDummyScene(luminary: Luminary) {
    if (!isSceneEditor()) {
        throw new Error(
            'Dummy scene (used for transition preview) is only available on scene editor.',
        );
    }
    const index = 0;
    const sceneGroup = luminary.sceneGroups[index];
    return sceneGroup.scenes[0];
}

export function findStandardTransitionNameByUrl(url: string | null): string | null {
    if (!url) {
        return null;
    }
    for (const transitionName in STANDARD_TRANSITIONS) {
        const aspectRatios = STANDARD_TRANSITIONS[transitionName];
        for (const aspect in aspectRatios) {
            if (aspectRatios[aspect] === url) {
                return transitionName;
            }
        }
    }
    return null;
}
